// Agent-facing Coaching Card — top 2 growth areas from V1.2 shadow grades.
// Drop this into the agent dashboard. Read-only, personal.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, TrendingUp, Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoachingCard() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/coaching/my-growth-areas?days=30`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(r.data);
      } catch { setData(null); }
      finally { setLoading(false); }
    };
    if (token) load();
  }, [token]);

  if (loading) {
    return (
      <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5 flex items-center gap-2" data-testid="coaching-card-loading">
        <Loader2 className="w-4 h-4 text-white/40 animate-spin" />
        <span className="text-white/50 text-sm">Loading your coaching focus…</span>
      </div>
    );
  }

  if (!data || !data.gradeable) {
    const reason = data?.reason;
    const isInsufficient = reason === 'insufficient_sample' || reason === 'no_calls_in_window';
    return (
      <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5" data-testid="coaching-card-empty">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Sparkles className="w-4 h-4 text-teal-400" />
          <h3 className="text-white font-semibold">Your coaching focus</h3>
          {isInsufficient && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-500/30"
              data-testid="coaching-insufficient-sample"
            >
              <AlertCircle className="w-3 h-3" />
              Insufficient Sample
            </span>
          )}
        </div>
        <p className="text-white/60 text-sm">{data?.message || 'No coaching data yet — keep taking calls and your growth areas will appear here.'}</p>
        {isInsufficient && data?.calls_needed > 0 && (
          <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2">
            <div className="text-white/50 text-xs">Progress</div>
            <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-teal-500 rounded-full transition-all"
                style={{ width: `${Math.min(100, ((data.sample_size || 0) / (data.min_samples_required || 3)) * 100)}%` }}
              />
            </div>
            <div className="text-teal-300 text-xs font-semibold">
              {data.sample_size || 0}/{data.min_samples_required || 3} calls
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-xl p-5" data-testid="coaching-card">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-teal-400" />
          <h3 className="text-white font-semibold">Your coaching focus</h3>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-white/40">Avg score</div>
          <div className="text-teal-300 font-semibold text-lg">{data.avg_overall_score ?? '—'}<span className="text-white/40 text-xs">/100</span></div>
        </div>
      </div>
      <p className="text-white/60 text-xs mb-4">{data.message}</p>
      <div className="space-y-3">
        {data.growth_areas.map((g, idx) => (
          <div key={g.criterion_key} className="bg-white/5 border border-white/10 rounded-lg p-3" data-testid={`coaching-growth-${idx}`}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-teal-600 text-white text-xs font-bold flex items-center justify-center">{idx + 1}</div>
                <div className="text-white font-medium text-sm">{g.label}</div>
              </div>
              <div className="text-right">
                <span className="text-amber-300 font-semibold text-sm">{g.mean_score_0_10}<span className="text-white/40 text-xs">/10</span></span>
                <div className="text-white/30 text-[10px]">across {g.n_calls} calls</div>
              </div>
            </div>
            <div className="flex items-start gap-2 mt-2 pl-8">
              <TrendingUp className="w-3.5 h-3.5 text-teal-400 shrink-0 mt-0.5" />
              <p className="text-white/70 text-xs leading-relaxed">{g.tip}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
